import { AdyenCheckout, Dropin } from "@adyen/adyen-web/auto";
import {
    ADYEN_CANCEL_ERROR_NAME,
    ADYEN_RESULT_CODE,
    PAGE_STATE_POLL_INTERVAL_MILLISECONDS,
    PAYMENT_LINK_STATE,
} from "./paymentPage.constants";
import {
    fetchPaymentLinkPage,
    openCheckoutSession,
    sendBankCheckStart,
    sendPaymentDetails,
    sendPaymentResult,
    startPaymentAttempt,
} from "./paymentPage.api";
import { readStoredAttempt, writeStoredAttempt } from "./paymentPageAttempt.store";
import {
    buildCheckoutConfiguration,
    buildDropinConfiguration,
    buildOrderSummary,
} from "./paymentPageCheckout.configuration";
import {
    PAGE_VIEW,
    resolveViewForDeclinedLink,
    resolveViewForLinkState,
    resolveViewForLoadError,
    resolveViewForPaymentError,
    resolveViewForPaymentResult,
} from "./paymentPage.views";

const TEXT_TYPE = "string";

const OBJECT_TYPE = "object";

export const NO_DROPIN = Object.freeze({ unmount: Function.prototype });

const NO_POLLING_TO_STOP = Function.prototype;

const VIEW_WHEN_PAID_WHILE_WAITING = new Map([
    [PAGE_VIEW.CHECKING_PAYMENT, PAGE_VIEW.PAYMENT_SUCCESSFUL],
    [PAGE_VIEW.PAYMENT_IN_PROGRESS, PAGE_VIEW.LINK_PAID],
]);

function writeBackgroundFailureToConsole(error) {
    console.error(error);
}

function hasPaymentResult(paymentResult) {
    return typeof paymentResult === OBJECT_TYPE;
}

function hasSessionResult(paymentResult) {
    return typeof paymentResult.sessionResult === TEXT_TYPE;
}

async function openDropin(pageContext, paymentLinkPage, checkoutSession) {
    const checkout = await AdyenCheckout(
        buildCheckoutConfiguration(paymentLinkPage, checkoutSession, pageContext.dropinCallbacks)
    );
    const dropin = new Dropin(checkout, buildDropinConfiguration(paymentLinkPage));
    dropin.mount(pageContext.dropinContainer.current);
    pageContext.dropinInstance.current = dropin;
}

export async function openPaymentForm(pageContext) {
    try {
        const paymentLinkPage = await fetchPaymentLinkPage(pageContext.paymentLinkId);
        if (paymentLinkPage.state !== PAYMENT_LINK_STATE.OPEN) {
            pageContext.updateView(resolveViewForLinkState(paymentLinkPage.state));
            return;
        }
        pageContext.updateOrderSummary(buildOrderSummary(paymentLinkPage));
        const checkoutSession = await openCheckoutSession(pageContext.paymentLinkId);
        await openDropin(pageContext, paymentLinkPage, checkoutSession);
        pageContext.updateView(PAGE_VIEW.PAYMENT_FORM);
    } catch (error) {
        pageContext.updateView(resolveViewForLoadError(error));
    }
}

export async function openPaymentFormAgain(pageContext) {
    pageContext.dropinInstance.current.unmount();
    pageContext.dropinInstance.current = NO_DROPIN;
    pageContext.updateView(PAGE_VIEW.LOADING);
    await openPaymentForm(pageContext);
}

export function runPaymentPageReload() {
    window.location.reload();
}

export async function startGatedPayment(pageContext, paymentData, component, actions) {
    try {
        const startedAttempt = await startPaymentAttempt(pageContext.paymentLinkId, component.core.session);
        writeStoredAttempt(pageContext.paymentLinkId, startedAttempt.attemptId);
        actions.resolve({ ...paymentData, sessionData: startedAttempt.sessionData });
    } catch (error) {
        actions.reject();
        pageContext.updateView(resolveViewForPaymentError(error));
    }
}

export async function sendBankCheckStartForAttempt(pageContext) {
    const storedAttempt = readStoredAttempt(pageContext.paymentLinkId);
    if (storedAttempt.isStored === false) {
        return;
    }
    try {
        await sendBankCheckStart(pageContext.paymentLinkId, storedAttempt.attemptId);
    } catch (error) {
        writeBackgroundFailureToConsole(error);
    }
}

export async function sendBankCheckDetails(pageContext, detailsState, actions) {
    const storedAttempt = readStoredAttempt(pageContext.paymentLinkId);
    try {
        const detailsResult = await sendPaymentDetails(
            pageContext.paymentLinkId,
            storedAttempt.attemptId,
            detailsState.data
        );
        actions.resolve(detailsResult);
    } catch (error) {
        actions.reject();
        pageContext.updateView(resolveViewForPaymentError(error));
    }
}

async function sendResultForAttempt(pageContext, paymentResult) {
    const storedAttempt = readStoredAttempt(pageContext.paymentLinkId);
    if (hasSessionResult(paymentResult) === false) {
        return;
    }
    if (storedAttempt.isStored === false) {
        return;
    }
    try {
        await sendPaymentResult(pageContext.paymentLinkId, storedAttempt.attemptId, paymentResult.sessionResult);
    } catch (error) {
        writeBackgroundFailureToConsole(error);
    }
}

async function updateViewForDeclinedLink(pageContext) {
    try {
        const paymentLinkPage = await fetchPaymentLinkPage(pageContext.paymentLinkId);
        pageContext.updateView(resolveViewForDeclinedLink(paymentLinkPage.state));
    } catch (error) {
        writeBackgroundFailureToConsole(error);
    }
}

async function updateViewForPaymentResult(pageContext, resultCode) {
    pageContext.updateView(resolveViewForPaymentResult(resultCode));
    if (resultCode === ADYEN_RESULT_CODE.REFUSED) {
        await updateViewForDeclinedLink(pageContext);
    }
}

export async function processPaymentResult(pageContext, paymentResult) {
    if (hasPaymentResult(paymentResult) === false) {
        return;
    }
    await sendResultForAttempt(pageContext, paymentResult);
    await updateViewForPaymentResult(pageContext, paymentResult.resultCode);
}

export function handleDropinError(pageContext, error) {
    if (error.name === ADYEN_CANCEL_ERROR_NAME) {
        return;
    }
    pageContext.updateView(PAGE_VIEW.CHECKING_PAYMENT);
}

async function processRedirectPayment(pageContext, paymentLinkPage) {
    if (paymentLinkPage.state === PAYMENT_LINK_STATE.PAID) {
        pageContext.updateView(PAGE_VIEW.PAYMENT_SUCCESSFUL);
        return;
    }
    if (paymentLinkPage.state !== PAYMENT_LINK_STATE.OPEN) {
        pageContext.updateView(resolveViewForLinkState(paymentLinkPage.state));
        return;
    }
    pageContext.updateOrderSummary(buildOrderSummary(paymentLinkPage));
    const storedAttempt = readStoredAttempt(pageContext.paymentLinkId);
    const detailsResult = await sendPaymentDetails(pageContext.paymentLinkId, storedAttempt.attemptId, {
        details: { redirectResult: pageContext.redirectResult.value },
    });
    await updateViewForPaymentResult(pageContext, detailsResult.resultCode);
}

function removeRedirectResultFromAddress() {
    window.history.replaceState(window.history.state, document.title, window.location.pathname);
}

export async function loadPaymentPage(pageContext) {
    if (pageContext.redirectResult.isPresent === false) {
        await openPaymentForm(pageContext);
        return;
    }
    removeRedirectResultFromAddress();
    try {
        await processRedirectPayment(pageContext, await fetchPaymentLinkPage(pageContext.paymentLinkId));
    } catch (error) {
        pageContext.updateView(resolveViewForPaymentError(error));
    }
}

async function handleWaitingEnded(pageContext, pageView) {
    if (pageView === PAGE_VIEW.CHECKING_PAYMENT) {
        pageContext.updateView(PAGE_VIEW.PAYMENT_FAILED);
        return;
    }
    await openPaymentFormAgain(pageContext);
}

async function applyPolledPageState(pageContext, pageView, paymentLinkPage) {
    if (paymentLinkPage.state === PAYMENT_LINK_STATE.PAID) {
        pageContext.updateView(VIEW_WHEN_PAID_WHILE_WAITING.get(pageView));
        return;
    }
    if (paymentLinkPage.state !== PAYMENT_LINK_STATE.OPEN) {
        pageContext.updateView(resolveViewForLinkState(paymentLinkPage.state));
        return;
    }
    if (paymentLinkPage.isPaymentInProgress) {
        return;
    }
    await handleWaitingEnded(pageContext, pageView);
}

async function updatePolledPageState(pageContext, pageView) {
    try {
        await applyPolledPageState(pageContext, pageView, await fetchPaymentLinkPage(pageContext.paymentLinkId));
    } catch (error) {
        writeBackgroundFailureToConsole(error);
    }
}

export function startPageStatePolling(pageContext, pageView) {
    if (pageView.isPollingPageState === false) {
        return NO_POLLING_TO_STOP;
    }
    const intervalId = window.setInterval(
        updatePolledPageState,
        PAGE_STATE_POLL_INTERVAL_MILLISECONDS,
        pageContext,
        pageView
    );
    return window.clearInterval.bind(window, intervalId);
}
